import { type TagColorType } from "components/DesignSystem/Tag/Tag";
import { type PendingSectionKey } from "store/apis/actionItems";

export type PendingBehavior =
  | "MESSAGE"
  | "PROFILE"
  | "CART"
  | "STATE_COMPLIANCE"
  | "FILING"
  | "INVOICES"
  | "W_FORMS";

export type PendingAccent = "purple" | "red" | "orange" | "neutral";

export type SectionConfig = {
  behavior: PendingBehavior;
  accent: PendingAccent;
  tag?: { label: string; tone: TagColorType };
  actionLabel: string;
  titleSingular?: string;
  titlePlural?: string;
  staticTitle?: string;
  staticSubtitle?: string;
  useBucketSubtitle?: boolean;
};

export const ACCENT_CLASS: Record<PendingAccent, string> = {
  purple: "t-bg-purple-40",
  red: "t-bg-red-30",
  orange: "t-bg-orange-30",
  neutral: "t-bg-neutral-20",
};

// The onboarding row is not one of this endpoint's sections: it renders first,
// driven by product onboarding progress.
export const COMPLETE_ONBOARDING_CONFIG: SectionConfig = {
  behavior: "PROFILE",
  accent: "purple",
  actionLabel: "Complete profile",
  staticSubtitle:
    "Upload the required documents to discover all applicable filings",
};

export type ApiPendingSectionKey = Exclude<
  PendingSectionKey,
  "PENDING_DOCUMENTS"
>;

// Render order is fixed on the FE (not the backend `order` field).
export const PENDING_SECTION_ORDER: ApiPendingSectionKey[] = [
  "OVERDUE",
  "DUE_IN_2_WEEKS",
  "STATE_COMPLIANCE",
  "CART_PAYMENT_PENDING",
  "DUE_LATER",
  "INVOICES_DUE",
  "W_FORMS_PENDING",
  "FILING_IN_ADD_DETAILS_CUSTOM_TYPE",
];

// This map only holds the presentational + behavior config keyed by section.
export const PENDING_SECTION_CONFIG: Record<
  ApiPendingSectionKey,
  SectionConfig
> = {
  OVERDUE: {
    behavior: "MESSAGE",
    accent: "red",
    tag: { label: "Includes penalty", tone: "red" },
    actionLabel: "Resolve",
    titleSingular: "overdue task pending",
    titlePlural: "overdue tasks pending",
    useBucketSubtitle: true,
  },
  DUE_IN_2_WEEKS: {
    behavior: "MESSAGE",
    accent: "orange",
    actionLabel: "Resolve",
    titleSingular: "task due in 2 weeks",
    titlePlural: "tasks due in 2 weeks",
    useBucketSubtitle: true,
  },
  CART_PAYMENT_PENDING: {
    behavior: "CART",
    accent: "orange",
    actionLabel: "Review & Pay",
    staticTitle: "Review and Pay for filings detected",
    staticSubtitle:
      "We've identified a few more filings based on your company information. Review and pay to launch them.",
  },
  DUE_LATER: {
    behavior: "MESSAGE",
    accent: "neutral",
    actionLabel: "Resolve",
    titleSingular: "task due later",
    titlePlural: "tasks due later",
    useBucketSubtitle: true,
  },
  STATE_COMPLIANCE: {
    behavior: "STATE_COMPLIANCE",
    accent: "orange",
    actionLabel: "Resolve",
    titleSingular: "state compliance task pending",
    titlePlural: "state compliance tasks pending",
    staticSubtitle:
      "Stay compliant by staying on top of state registrations and filings",
  },
  FILING_IN_ADD_DETAILS_CUSTOM_TYPE: {
    behavior: "FILING",
    accent: "neutral",
    actionLabel: "Resolve",
    titleSingular: "filing needs details",
    titlePlural: "filings need details",
    staticSubtitle: "Add the missing details to proceed with your filings",
  },
  INVOICES_DUE: {
    behavior: "INVOICES",
    accent: "neutral",
    actionLabel: "Resolve",
    titleSingular: "invoice due",
    titlePlural: "invoices due",
    staticSubtitle: "Clear your pending invoices to stay on track",
  },
  W_FORMS_PENDING: {
    behavior: "W_FORMS",
    accent: "neutral",
    actionLabel: "Resolve",
    titleSingular: "W-form pending",
    titlePlural: "W-forms pending",
    staticSubtitle: "Collect the pending W-forms from your vendors",
  },
};
